import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { WORK_API } from './api';
import { extractErrorMessage } from '../../utils/apiErrors';
import { EmailDiagnosticDetails } from './EmailDiagnosticDetails';

const LABELS = { pending: 'Queued', sending: 'Sending', accepted: 'Accepted by email service', failed: 'Not sent', partial: 'Partially sent', blocked: 'Blocked before sending', cancelled: 'Cancelled', manual_review: 'Needs review' };

export const TicketEmailNotice = ({ ticket, canManage }) => {
  const [notice, setNotice] = useState(null), [error, setError] = useState(''), [busy, setBusy] = useState(false), [refresh, setRefresh] = useState(0);
  const endpoint = `${WORK_API}/tickets/${ticket.id}/submission-email`;
  useEffect(() => {
    let active = true, timer, count = 0;
    const controller = new AbortController();
    const read = async () => {
      try {
        const r = await axios.get(endpoint, { signal: controller.signal });
        if (!active) return;
        setNotice(r.data); setError('');
        if (r.data && ['pending', 'sending'].includes(r.data.status) && count++ < 45) timer = setTimeout(read, 2500);
      } catch (e) { if (active) setError(extractErrorMessage(e, 'Unable to check submission emails')); }
    };
    if (ticket.audit.some((a) => a.action === 'submit')) read(); else setNotice(null);
    return () => { active = false; controller.abort(); clearTimeout(timer); };
  }, [endpoint, ticket.revision, ticket.status, ticket.audit, refresh]);
  const retry = useCallback(async () => {
    if (busy) return;
    setBusy(true); setError('');
    try { const r = await axios.post(`${endpoint}/retry`); setNotice(r.data); setRefresh((v) => v + 1); }
    catch (e) { setError(extractErrorMessage(e, 'Unable to retry notification')); }
    finally { setBusy(false); }
  }, [busy, endpoint]);
  if (!notice && !error) return null;
  return <section className="border-y border-slate-200 py-4 space-y-3 min-w-0" data-testid="ticket-submission-email">
    <div className="flex flex-wrap gap-3 items-center justify-between"><h2 className="text-base font-semibold flex items-center gap-2" data-testid="ticket-email-heading"><Mail size={16} />Submission emails</h2>
      <div className="flex flex-wrap gap-2"><Button size="sm" variant="ghost" onClick={() => setRefresh((v) => v + 1)} disabled={busy} data-testid="ticket-email-refresh"><RefreshCw size={14} />Refresh</Button>
        {notice?.can_retry && <Button size="sm" variant="outline" disabled={busy} onClick={retry} data-testid="ticket-email-retry">{busy ? 'Retrying…' : 'Retry emails'}</Button>}</div></div>
    {error && <p role="alert" className="text-sm text-red-700 break-words" data-testid="ticket-email-error">{error}</p>}
    {notice && <>
      <p className="text-sm text-slate-600" data-testid="ticket-email-status">{LABELS[notice.status] || notice.status}</p>
      {notice.status === 'blocked' && <EmailDiagnosticDetails diagnostic={notice.diagnostic} />}
      <ul className="space-y-2 text-sm min-w-0">{notice.recipients.map((r) => <li key={r.id} className="min-w-0 [overflow-wrap:anywhere]" data-testid={`ticket-email-recipient-${r.id}`}>
        <span className="font-medium">{r.roles.join(' / ')}</span> · {r.email} · <span data-testid={`ticket-email-recipient-status-${r.id}`}>{LABELS[r.status] || r.status}</span>
        {r.message && <p className="text-xs text-slate-500 mt-1" data-testid={`ticket-email-recipient-message-${r.id}`}>{r.message}</p>}
      </li>)}</ul>
      {notice.warnings.filter((message) => !notice.diagnostic || (message !== notice.diagnostic.message && !notice.diagnostic.issues?.some((issue) => issue.message === message))).map((message, i) => <p key={i} role="status" className="text-sm text-amber-800 break-words" data-testid={`ticket-email-warning-${i}`}>{message}</p>)}
      {canManage && notice.status === 'blocked' && (!notice.diagnostic || ['company_settings', 'recipients'].includes(notice.diagnostic.stage)) && <Link to="/field/work/settings" className="text-sm text-sky-700 underline" data-testid="ticket-email-settings-link">Company contact settings</Link>}
    </>}
  </section>;
};